import React, { useMemo, useCallback } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import sortBy from 'lodash/sortBy';
import take from 'lodash/take';
import filter from 'lodash/filter';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import some from 'lodash/some';
import styled from '@emotion/styled';
import flow from 'lodash/flow';
import head from 'lodash/head';
import has from 'lodash/has';
import includes from 'lodash/includes';
import compact from 'lodash/compact';
import last from 'lodash/last';
import groupBy from 'lodash/groupBy';
import { useTranslation } from 'react-i18next';
import { Col, Icon, LayoutCard, Link, Text, Title } from '@vmos2/design-system';
import { getAppType } from '@vmos2/core';
import { timeUtils, getDeliverooTime, menuStatus, openGoogleMapWindow, useDateFnsLocale } from '@vmos2/utils';
import { format, setDay, nextDay, max, isBefore, isAfter, parseISO } from 'date-fns';

import { AppStatuses, MenuStatus } from '../constants';
import { Menus } from './menus';
import { getDaySlotShift } from './getDaysSlotShift';
import { useSettings } from '@vmos2/providers';

type Props = {
  store: Object,
  settings?: {},
  menus?: {},
  isGeocodeEnabled?: boolean,
  location?: {
    lat: number,
    long: number,
  },
};

export const Store = ({ menus, store, settings, location, isGeocodeEnabled }: Props) => {
  const { t, i18n } = useTranslation('');
  const dateFnsLocale = useDateFnsLocale(i18n.language);
  const {
    timezone: tenantTimezone,
    preorderAvailableMenusLimit: limit,
    storeSelectionKilometers: distanceInKilometers,
  } = useSettings();
  const storeSettings = get(store, 'settings', {});

  const timezone = storeSettings?.timezone ?? tenantTimezone;
  const storeInfoSetting = get(storeSettings, 'store.information', get(settings, 'store.information'));
  const storePhoneNumberSetting = get(storeSettings, 'store.phone.number', get(settings, 'store.phone.number'));
  const strictMenuHours = !!+get(storeSettings, 'preorder.enable.strictmenuhours', '0');
  const {
    isMenuActive: isMenuActiveFromUtils,
    getCurrentWeekday,
    isWithinDates,
    calculateMenuOpeningHours,
    getCurrentDayWorkHours,
    getWeekdayWorkHours,
  } = timeUtils;
  const isOpat = getAppType() === 'opat';
  const isStoreOpatOff = get(store, 'appStatus.opat') === 'off';

  const futureDays = useMemo(() => {
    let value;
    switch (getAppType()) {
      case 'delivery':
        value = +get(storeSettings, 'preorder.delivery.availableDays', '1');
        break;
      case 'online':
        value = +get(storeSettings, 'preorder.clickAndCollect.availableDays', '1');
        break;
      default:
        value = 1;
    }
    return value;
  }, [storeSettings]);

  const daySlotShift = useCallback(
    menuUUID => getDaySlotShift(menuUUID, storeSettings, settings, timezone),
    [storeSettings, timezone]
  );

  const phoneNumber = useMemo(() => {
    let phoneNumber;

    try {
      const phoneJson = JSON.parse(storePhoneNumberSetting);

      phoneNumber =
        has(phoneJson, 'appTypesOnline') && includes(get(phoneJson, 'appTypesOnline', []), getAppType())
          ? get(phoneJson, 'phone')
          : null;
    } catch (e) {
      phoneNumber = !+storePhoneNumberSetting ? null : storePhoneNumberSetting;
    }

    return phoneNumber;
  }, [storePhoneNumberSetting]);

  const storeInfo = useMemo(() => {
    let storeInfo;

    try {
      const storeInfoJson = JSON.parse(storeInfoSetting);
      storeInfo = get(storeInfoJson, getAppType(), null);
    } catch (e) {
      storeInfo = isEmpty(trim(storeInfoSetting)) ? null : storeInfoSetting;
    }

    return storeInfo;
  }, [storeInfoSetting]);

  const availableTodayMenus = useMemo(() => {
    return flow([
      menus => filter(menus, menu => getWeekdayWorkHours(menu.availableHours)),
      menus => sortBy(menus, menu => getWeekdayWorkHours(menu.availableHours).from),
      menus => filter(menus, menu => !daySlotShift(menu.uuid)),
      menus => filter(menus, menu => isWithinDates(new Date(), get(menu, 'menuDates[0]'))),
      menus =>
        map(menus, menu => {
          const menuOpeningHours = calculateMenuOpeningHours(
            new Date(),
            getWeekdayWorkHours(menu.availableHours, new Date()),
            store.holidayHours
          );
          const isMenuActive = isMenuActiveFromUtils(
            getCurrentDayWorkHours(menu.availableHours, timezone) || [],
            strictMenuHours,
            timezone
          );
          const status = menuStatus(menuOpeningHours, false, timezone, true);

          if (isOpat && status === MenuStatus.PREORDER) {
            return null;
          }

          const updatedMenu = {
            ...menu,
            status,
            isMenuActive,
            menuOpeningHours,
            sortStatus: Object.values(MenuStatus).indexOf(status),
          };

          const latestMenuHours = last(sortBy(menuOpeningHours, 'to'));
          if (latestMenuHours?.to) {
            const [menuUntilHours, menuUntilMinutes] = latestMenuHours.to.split(':');
            const menuTillTime = new Date();
            menuTillTime.setMinutes(menuUntilMinutes);
            menuTillTime.setHours(Math.max(+menuUntilHours, 0));
            const menuUntilTimestamp = menuTillTime.getTime();
            updatedMenu.menuUntilTimestamp = menuUntilTimestamp;
          }

          return updatedMenu;
        }),
      menus => filter(menus, 'menuOpeningHours'),
      menus => sortBy(menus, 'menuUntilTimestamp'),
      menus => sortBy(menus, 'sortStatus'),
    ])(menus);
  }, [menus, strictMenuHours, timezone, daySlotShift]);

  const availableFutureDayMenus = useMemo(() => {
    if (isOpat) return [];
    const currentDay = getCurrentWeekday();
    const moveHoursTillTodayToEnd = (hours, x) => {
      const items = x % hours.length;
      for (let i = 0; i < items; i++) hours.push(hours[i]);
      hours.splice(0, items);
      return hours;
    };

    const availableDays = map(new Array(futureDays > 7 ? 7 : futureDays), (el, i) => {
      const day = currentDay + i;
      return day > 7 ? day - 7 : day;
    });

    return flow([
      menus => filter(menus, ({ uuid }) => !some(availableTodayMenus, m => m.uuid === uuid)),
      menus =>
        filter(menus, menu => {
          if (!isEmpty(menu.menuDates) && isAfter(new Date(), parseISO(get(menu, 'menuDates[0].dateTo')))) return false;

          if (!daySlotShift(menu.uuid)) {
            return futureDays > 1;
          }
          if (daySlotShift(menu.uuid) >= 1) return true;
          return true;
        }),
      menus =>
        map(menus, menu => {
          const shiftDays = map(new Array(futureDays > 7 ? 7 : futureDays), (el, i) => {
            const day = daySlotShift(menu.uuid) + i;
            return day > 7 ? day - 7 : day;
          });
          const availableHours = map(groupBy(get(menu, 'availableHours', []), 'weekday'), (hours, weekday) => {
            return calculateMenuOpeningHours(nextDay(new Date(), weekday), hours, store.holidayHours, false);
          })
            .flat()
            .filter(elm => elm);
          const sortedHours = sortBy(availableHours, 'weekday');
          const todayIndex = sortedHours.findIndex(v => {
            return v.weekday >= currentDay;
          });

          const index = Math.max(todayIndex, 0);

          let hoursByAvailableDays = moveHoursTillTodayToEnd(sortedHours, index);
          if (daySlotShift(menu.uuid)) {
            const reorder = (hours, availableDays) => {
              return reduce(
                availableDays,
                (acc, day) => {
                  if (some(hours, { weekday: day })) {
                    return acc.concat(filter(hours, { weekday: day }));
                  }
                  return acc;
                },
                []
              );
            };
            hoursByAvailableDays = reorder(sortedHours, shiftDays);
          }

          const sortedHoursByAvailableDays = sortBy(hoursByAvailableDays, ['from', 'to']);
          const nextWeekDay = get(head(hoursByAvailableDays), 'weekday', 0);
          const nextHours = filter(sortedHoursByAvailableDays, { weekday: nextWeekDay });
          const hasPreOrder = includes(availableDays, nextWeekDay) || includes(shiftDays, nextWeekDay);

          const futureWeekDay = nextWeekDay
            ? format(setDay(new Date(), nextWeekDay), 'iiii', { locale: dateFnsLocale })
            : null;

          const futureDate = nextWeekDay ? setDay(new Date(), nextWeekDay) : null;

          const closestNexWeekDay = nextDay(new Date(), nextWeekDay);
          if (isAfter(closestNexWeekDay, parseISO(get(menu, 'menuDates[0].dateTo')))) {
            return null;
          }

          const futureDay = () => {
            if (isEmpty(menu.menuDates)) return null;

            if (!daySlotShift(menu.uuid)) {
              return parseISO(menu.menuDates[0].dateFrom);
            }
            if (isWithinDates(closestNexWeekDay, get(menu, 'menuDates[0]'))) {
              return null;
            }

            if (isBefore(closestNexWeekDay, parseISO(get(menu, 'menuDates[0].dateTo')))) {
              return max([closestNexWeekDay, parseISO(get(menu, 'menuDates[0].dateFrom'))]);
            }
            return null;
          };

          return {
            ...menu,
            hours: hoursByAvailableDays,
            status: hasPreOrder ? MenuStatus.PREORDER : MenuStatus.CLOSED,
            menuOpeningHours: hasPreOrder ? nextHours : null,
            futureWeekDay,
            futureDate,
            futureDay: futureDay(),
          };
        }),
      menus => filter(menus, 'menuOpeningHours'),
      menus => compact(menus),
      menus => take(menus, limit - availableTodayMenus.length),
    ])(menus);
  }, [menus, futureDays, availableTodayMenus, limit, daySlotShift, isOpat]);

  const unavailableMenus = useMemo(() => {
    if (isOpat) return [];
    return flow([
      menus => filter(menus, ({ uuid }) => !some(availableTodayMenus, m => m.uuid === uuid)),
      menus => filter(menus, ({ uuid }) => !some(availableFutureDayMenus, m => m.uuid === uuid)),
      menus => filter(menus, ({ menuDates }) => isEmpty(menuDates)),
      menus => map(menus, menu => ({ ...menu, status: MenuStatus.CLOSED })),
    ])(menus);
  }, [menus, availableTodayMenus, availableFutureDayMenus, isOpat]);

  const isStoreDeliverooUnavailable =
    has(store.deliverooInfo, 'error') ||
    get(store.deliverooInfo, 'status') === 'closed' ||
    get(store.deliverooInfo, 'status') === 'unavailable';

  const isDeliveryAvailable =
    (!isEmpty(menus) && getAppType() === 'delivery' && !isEmpty(location) && !get(store, 'availableDelivery', false)) ||
    isStoreDeliverooUnavailable;

  const showDeliverooInfo = useMemo(() => {
    const hasClosedMenu =
      isEmpty(availableTodayMenus) ||
      availableTodayMenus.some(menu => menu.status === MenuStatus.CLOSED) ||
      isDeliveryAvailable;
    return store.deliverooInfo && !isStoreDeliverooUnavailable && !hasClosedMenu;
  }, [store, availableTodayMenus, isDeliveryAvailable]);

  const noActiveMenusToday = availableTodayMenus.every(menu => !menu.isMenuActive);

  if (isOpat && (noActiveMenusToday || isStoreOpatOff)) {
    return null;
  }

  if (isOpat && isEmpty(availableTodayMenus) && !isEmpty(menus)) {
    return null;
  }

  if (
    isEmpty(availableTodayMenus) &&
    isEmpty(availableFutureDayMenus) &&
    isEmpty(unavailableMenus) &&
    !isEmpty(menus)
  ) {
    return null;
  }

  return (
    <Wrapper
      key={store.uuid}
      tag={LayoutCard}
      xs={10}
      xsOffset={1}
      sm={10}
      smOffset={1}
      md={10}
      mdOffset={1}
      data-test="store-container"
    >
      <Header data-test="store-header">
        <Title data-test="store-name" mode="PanelHeader" level={2} margin="none">
          {store.name}
        </Title>
        {!isEmpty(location) && (
          <Distance data-test="store-distance">
            {!distanceInKilometers
              ? t('geoLocation:milesAway', { count: store.miles || '<0.1' })
              : t('geoLocation:kmAway', { count: store.km })}
          </Distance>
        )}
      </Header>

      <Info data-test={`store-${store.name}-address`}>
        <IconWrapper name="MapMarker" />

        <ViewMapLinkButton
          data-test={`store-${store.name}-address-btn`}
          onClick={() => {
            openGoogleMapWindow(
              store.googlePlaceId
                ? `https://www.google.com/maps/place/?q=place_id:${store.googlePlaceId}`
                : `https://www.google.com/maps/search/?api=1&query=${store.address}`
            );
          }}
          title={store.name}
        >
          {store.address}
        </ViewMapLinkButton>
      </Info>

      {showDeliverooInfo && (
        <Info data-test={`store-${store.name}-deliveroo`}>
          <IconWrapper name="Delivery" />
          {`${getDeliverooTime(store.deliverooInfo)} minutes`}
        </Info>
      )}

      {phoneNumber && (
        <Info data-test={`store-${store.name}-information`}>
          <IconWrapper name="Phone" />
          <Link url={`tel:${phoneNumber.replace(/[()\s]/g, '')}`}>{phoneNumber}</Link>
        </Info>
      )}

      {storeInfo && (
        <Info data-test={`store-${store.name}-information`}>
          <IconWrapper name="InfoCircle" />
          {storeInfo}
        </Info>
      )}

      {isDeliveryAvailable && (
        <Info data-test={`store-${store.name}-unavailable-delivery`}>
          <IconWrapper name="TimesCircle" mode="danger" />
          <Text mode="StoreSelection:UnavailableDelivery">
            {t(isGeocodeEnabled ? 'delivery:unavailableDeliveryAddress' : 'delivery:unavailableDelivery')}
          </Text>
        </Info>
      )}

      {isEmpty(menus) && (
        <Info data-test={`store-${store.name}-has-no-menus`}>
          <IconWrapper name="TimesCircle" mode="danger" />
          <Text mode="StoreSelection:UnavailableDelivery">
            {t('storeSelection:doesntOffer', { channel: t(`storeSelection.channel:${getAppType()}`) })}
          </Text>
        </Info>
      )}
      <Menus
        menus={menus}
        storeUUID={store.uuid}
        storeName={store.name}
        storeAppStatus={get(store, ['appStatus', getAppType()], AppStatuses.ON)}
        availableTodayMenus={availableTodayMenus}
        availableFutureDayMenus={availableFutureDayMenus}
        unavailableMenus={unavailableMenus}
        strictMenuHours={!!+get(storeSettings, 'preorder.enable.strictmenuhours', '0')}
        isDeliveryAvailable={isDeliveryAvailable}
        holidayHours={store.holidayHours}
      />
    </Wrapper>
  );
};

const Wrapper = styled(Col)`
  margin-bottom: 32px;
  color: ${({ theme }) => theme.color.grey};
  box-shadow: ${({ theme }) => theme.boxShadow.small};
  border-radius: ${({ theme }) => theme.borderRadius.large};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Header = styled('div')`
  display: flex;
  padding-bottom: 8px;
  justify-content: space-between;
`;

const Distance = styled('div')`
  flex: none;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  margin-left: 4px;

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;

const Info = styled('div')`
  display: flex;
  margin-top: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.xs};
  }

  a {
    color: ${({ theme }) => theme.color.grey};
  }
`;

const IconWrapper = styled(Icon)`
  margin-right: 8px;
  flex: 0 0 ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme, color }) => theme.color[color]};
`;

const ViewMapLinkButton = styled('button')`
  text-align: left;
  background: none;
  border: none;
  outline: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
`;
